import { Pressable, StyleProp, StyleSheet, View, Text, ViewStyle } from "react-native";
import { CheckSquare, ExternalLink, Square } from "lucide-react";
import { useState } from "react";
import { colors, spacing, radii, typographyScale } from "../../../../styles/design-system";

export interface TaskChipProps {
  name: string;
  category: string;
  duedate?: string | Date | number;
  isDone: boolean;
  style?: StyleProp<ViewStyle>;
  navigateToDetail?: () => void;
}

export const TaskChip = ({ name, category, duedate, isDone = false, style, navigateToDetail }: TaskChipProps) => {
  const [isHover, setIsHover] = useState(false);
  const combinedStyles = [styles.taskChip, ...(style ? [style] : []), ...(isHover ? [styles.hovered] : [])];
  return (
    <Pressable
      style={combinedStyles}
      onPress={navigateToDetail}
      onMouseEnter={() => {
        setIsHover(true);
      }}
      onMouseLeave={() => {
        setIsHover(false);
      }}>
      <View style={styles.taskStatus}>
        {isDone ? (
          <CheckSquare size={20} style={styles.doneStatusIcon} />
        ) : (
          <Square size={20} style={styles.undoneStatusIcon} />
        )}
      </View>
      <View style={styles.taskContent}>
        <Text style={styles.taskName}>{name}</Text>
        <View style={styles.taskMetaData}>
          <Text style={styles.metaTextStyle}>{category}</Text>
          {duedate != null && (
            <>
              <Text style={styles.metaTextStyle}>•</Text>
              <Text
                style={
                  styles.metaTextStyle
                }>{`Due ${new Date(duedate).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "2-digit" })}`}</Text>
            </>
          )}
        </View>
        {navigateToDetail != null && <Text style={styles.navigateText}>Click to view in To-dos →</Text>}
      </View>
      {isHover && <ExternalLink size={16} style={styles.externalLinkIcon} />}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  taskChip: {
    position: "relative",
    width: "fit-content",
    padding: spacing.sm,
    borderRadius: radii.xl,
    display: "flex",
    flexDirection: "row",
    gap: 12,
    borderWidth: 2,
    borderColor: "#9aaffe",
    backgroundColor: colors.brand[50],
  },
  hovered: {
    backgroundColor: colors.brand[100],
  },
  taskStatus: {
    alignSelf: "flex-start",
    padding: 10,
    borderRadius: radii.lg,
    backgroundColor: colors.brand[500],
  },
  doneStatusIcon: {
    color: colors.white,
  },
  undoneStatusIcon: {
    color: colors.white,
  },
  taskContent: {
    flex: 1,
    maxWidth: 360,
    paddingRight: 30,
  },
  taskName: {
    color: colors.grey[900],
    fontWeight: 600,
    fontSize: typographyScale.textSm.fontSize,
  },
  taskMetaData: { display: "flex", flexDirection: "row", gap: spacing.xs, marginTop: spacing.xs },
  metaTextStyle: { fontSize: typographyScale.textXs.fontSize, color: colors.grey[600] },
  navigateText: {
    marginTop: spacing.xs,
    color: colors.brand[500],
    fontWeight: 500,
    fontSize: typographyScale.textXs.fontSize,
  },
  externalLinkIcon: {
    position: "absolute",
    top: spacing.sm,
    right: spacing.sm,
    padding: spacing.xxs,
    color: colors.brand[500],
  },
});
